const formValidation = require('../components/formValidation');

/**
 * AddressService - handles communication with the server
 * - constructs the URL using the provided parameters
 * - executes the fetch request
 * - returns the address suggestions
 * @param {country, maxResults}
 */
class AddressService {
  constructor({ country, maxResults } = {}) {
    this.country = country;
    this.maxResults = maxResults;
    this.available = true;
  }
  buildUrl(fieldValues) {
    const params = { country: this.country };

    for (const [key, val] of Object.entries(fieldValues)) {
      if (val) params[key] = val;
    }

    const query = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

    const baseUrl = window.spsDataLayer.endpoints.addressAutoCompleteEndpoint;
    return `${baseUrl}?${query}`;
  }

   async fetch(fieldValues) {
    if (!this.available) return [];

    try {
      const res = await fetch(this.buildUrl(fieldValues), {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!res.ok) {
        this.available = false;
        return [];
      }

      const data = await res.json();

      if (!data.serviceEnabled) {
        this.available = false;
        return [];
      }

      if (!Array.isArray(data.addressAutoCompleteResponse)) {
        console.warn('[AddressAC] Ungültige Response:', data.addressAutoCompleteResponse);
        return [];
      }

      return (data.addressAutoCompleteResponse ?? []).slice(0, this.maxResults);
    } catch (err) {
      this.available = false;
      console.warn('[AddressAC] Service deaktiviert:', err);
      return [];
    }
  }
}

/**
 * Dropdown - handles the display of address suggestions and user interaction with them
 * - renders the suggestions in a dropdown below the active input field
 * - notifies the controller when a suggestion is selected (onSelect)
 * @param {string} cls - CSS class for the dropdown element
 */
class Dropdown {
  #results = [];
  #el;
  #currentAnchor = null;

  constructor() {
    this.#el = document.createElement('div');
    this.#el.className = 'cc-address-ac';

    this.onSelect = null;
    this.#bindClick();
  }

  attachTo(input) {
    if (this.#currentAnchor === input) return;

    this.close();
    this.#currentAnchor = input;

    const wrap = input.closest('.form-group') ?? input.parentElement;
    wrap.style.position = 'relative';
    wrap.appendChild(this.#el);
  }

  #bindClick() {
    this.#el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const item = e.target.closest('.cc-address-ac__item');
      if (!item || !this.onSelect) return;

      const idx = parseInt(item.dataset.idx, 10);
      if (this.#results[idx]) {
        this.onSelect(this.#results[idx]);
      }
    });
  }

  render(results) {
    this.#results = results;

    if (!results.length) {
        this.close();
        return;
    }

    const template = document.getElementById('cc-address-ac-item-template');
    const fragment = document.createDocumentFragment();

    results.forEach((a, i) => {
        const clone = template.content.cloneNode(true);
        const item = clone.querySelector('.cc-address-ac__item');
        item.dataset.idx = i;
        item.querySelector('.cc-address-ac__postalcode').textContent = a.postalCode ?? '';
        item.querySelector('.cc-address-ac__city').textContent = a.city ?? '';
        item.querySelector('.cc-address-ac__street').textContent = a.street ?? '';
        fragment.appendChild(clone);
    });

    this.#el.innerHTML = '';
    this.#el.appendChild(fragment);
    this.#el.classList.add('open');
  }

  open() {
    if (this.#results.length) {
      this.#el.classList.add('open');
    }
  }

  close() {
    this.#el.classList.remove('open');
    this.#el.innerHTML = '';
    this.#results = [];
  }

  get isOpen() {
    return this.#el.classList.contains('open');
  }
}

/**
 * DebouncedField - encapsulates the logic for an input field with debounced search
 * - signals to the controller when this field is active (onActivity)
 * - performs the search with debounce (onSearch)
 * - signals to the controller when the field is exited (onBlur)
 * @param {selector, paramKey, minChars, debounceMs}
 */
class DebouncedField {
  #timer = null;
  #reqId = 0;

  constructor({ selector, selectors, paramKey, minChars, debounceMs }) {
    this.paramKey = paramKey;
    this.minChars = minChars;
    this.debounceMs = debounceMs; 
    this.input = document.querySelector(selector);
    this.selectors = selectors;
    this.patternCheck = false;

    if (!this.input) {
      console.warn('[AddressAC] Input nicht gefunden:', selector);
      return;
    }

    this.onSearch = null;  
    this.onActivity = null;
    this.onBlur = null;
  }

  init() {
    if (!this.input) return;

    this.input.addEventListener('input', () => this.#handleInput());

    this.input.addEventListener('blur', () => {
      this.onBlur?.();
    });

    this.input.addEventListener('focus', () => {
      if (this.value.length >= this.minChars) {
        this.onActivity?.(this);
      }
    });
  }

  #handleInput() {
    const inputs = document.querySelectorAll(this.selectors);
    const val = this.value;

    clearTimeout(this.#timer);

    if (val.length < this.minChars) {
      this.onBlur?.();
      return;
    }
    
    // validate this and other dependent field values before send to the autocomplete provider (NOA-5546)
    inputs.forEach((el) => {
      let pattern = el.getAttribute('pattern');
      const val = el.value;
 
      // The ZIP code is an exception here. In this case, the length check—which is not important to the provider—is removed. 
      if (el.classList.contains('shippingZipCode')) {
        pattern = pattern.replace(`{${formValidation.POSTCODE_LENGTHS[window.spsDataLayer.internal.global.currentCountry]}}$`, '');
      }

      if (pattern && val && !new RegExp(pattern).test(val)) {
        this.onBlur?.();
        this.patternCheck = true;
        return;
      }
    });

    if (this.patternCheck) {
      this.patternCheck = false;
      return;
    }

    this.onActivity?.(this);

    const currentReq = ++this.#reqId;

    this.#timer = setTimeout(async () => {
      if (!this.onSearch) return;

      const results = await this.onSearch(this.paramKey, val);

      if (currentReq !== this.#reqId) return;
      this.onSearchComplete?.(results);
    }, this.debounceMs);
  }

  get value() {
    return this.input?.value.trim() ?? '';
  }

  set value(val) {
    if (!this.input) return;
    this.input.value = val;
  }
}

/**
* AddressAutocomplete - coordinates the interaction between the fields, the dropdown, and the service
* - initializes the DebouncedFields and the dropdown
* - responds to field activity (onActivity) and displays the dropdown
* - responds to field search queries (onSearch) and calls the service
* - responds to the selection of a suggestion in the dropdown (onSelect) and populates the fields accordingly
 * @param {country, maxResults}
 */
class AddressAutocomplete {
  #dropdown;
  #blurTimer = null;
  #applying = false;

  constructor(config) {
    if (!config) throw new Error('[AddressAC] Config mandatory');

    const {
      debounceMs = 300,
      minChars = {
        postalCode: 2,
        city: 2,
        street: 3,
      },
      maxResults = 10,
      allowedCountries = ['DE', 'AT', 'CH'],
      postalCodeSelector,
      citySelector,
      streetSelector,
      countrySelector,
    } = config;

    if (!postalCodeSelector || !citySelector || !streetSelector || !countrySelector) {
      throw new Error('[AddressAC] postalCodeSelector, citySelector, streetSelector and countrySelector are required');
    }

    this.countryInput = document.querySelector(countrySelector);
    this.allowedCountries = allowedCountries;

    this.service = new AddressService({country: this.country, maxResults });
    this.#dropdown = new Dropdown();
    this.fields = {
      postalCode: new DebouncedField({
        selector: postalCodeSelector,
        selectors: `${postalCodeSelector}, ${citySelector}, ${streetSelector}`,
        paramKey: 'postalCode',
        minChars: minChars.postalCode,
        debounceMs,
      }),
      city: new DebouncedField({
        selector: citySelector,
        selectors: `${postalCodeSelector}, ${citySelector}, ${streetSelector}`,
        paramKey: 'city',
        minChars: minChars.city,
        debounceMs,
      }),
      street: new DebouncedField({
        selector: streetSelector,
        selectors: `${postalCodeSelector}, ${citySelector}, ${streetSelector}`,
        paramKey: 'street',
        minChars: minChars.street,
        debounceMs,
      }),
    };
  }

  init() {
    for (const field of Object.values(this.fields)) {
      // Search → Call service
      field.onSearch = (paramKey, value) => {
        if (this.#applying) return Promise.resolve([]);
        return this.#search(paramKey, value);
      };

      // Active → Attach dropdown to the active field
      field.onActivity = (activeField) => {
        clearTimeout(this.#blurTimer);
        this.#dropdown.attachTo(activeField.input);
      };

      // Search result → Render dropdown
      field.onSearchComplete = (results) => {
        this.#dropdown.render(results);
      };

      // Blur → Close dropdown (with a delay so that clicks on dropdown items are still registered)
      field.onBlur = () => {
        this.#blurTimer = setTimeout(() => this.#dropdown.close(), 150);
      };

      field.init();
    }

    this.#dropdown.onSelect = (addr) => this.#applyAddress(addr);
  }

  get country() {
    return this.countryInput?.value?.toUpperCase() ?? '';
  }

  get isActive() {
    return this.allowedCountries.includes(this.country);
  }

  #search(triggerParam, triggerValue) {
    if (!this.isActive) return Promise.resolve([]);

    this.service.country = this.country;
    const fieldValues = {};

    // From UX requested search optimization. When the user is typing in the steet field and postalcode + city is already provide, we only send the street and city  as a parameter to the service, because this usually leads to broader results. In all other cases we send all available parameters.
    if (triggerParam === 'street'
      && this.fields.postalCode.value
      && this.fields.city.value) {
      fieldValues.city = this.fields.city.value;
    } else {
      for (const field of Object.values(this.fields)) {
        fieldValues[field.paramKey] = field.value;
      }
    }

    fieldValues[triggerParam] = triggerValue;
    return this.service.fetch(fieldValues);
  }

  #applyAddress(addr) {
    this.#applying = true;
    
    if (addr.postalCode) this.fields.postalCode.value = addr.postalCode;
    if (addr.city) this.fields.city.value = addr.city;
    if (addr.street) this.fields.street.value = addr.street;
    
    this.#dropdown.close();
    
    for (const field of Object.values(this.fields)) {
      if (field.input && field.input.value) {
        field.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setTimeout(() => {
      this.#applying = false;
    }, 400);
  }
}

module.exports = AddressAutocomplete;